import { MDXComponents } from "@/components/shared/ui/mdx";
import { authStore } from "@/stores/auth.store";
import "@/styles/globals.css";
import { MDXProvider } from "@mdx-js/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "jotai";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { Geist, Geist_Mono as GeistMono, Newsreader } from "next/font/google";
import Head from "next/head";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

const Layout = dynamic(() => import("@/components/shared/Layouts/Layout"), { ssr: false });

const MaintenanceMode = dynamic(() => import("@/components/shared/MaintenanceMode"), { ssr: false });

const geist = Geist({
	subsets: ["latin"],
	weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
});

const geistMono = GeistMono({
	subsets: ["latin"],
	weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
});

const newsreader = Newsreader({
	subsets: ["latin"],
	weight: ["200", "300", "400", "500", "600", "700", "800"],
});

const GlobalStyles = () => (
	<style jsx global>
		{`
			:root {
				--geist-font: ${geist.style.fontFamily};
				--geist-mono-font: ${geistMono.style.fontFamily};
				--newsreader-font: ${newsreader.style.fontFamily};
			}
		`}
	</style>
);

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
		},
		mutations: {
			retry: 1,
		},
	},
});

export default function App({ Component, pageProps }: AppProps) {
	const isMaintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "true";
	if (isMaintenanceMode) {
		return (
			<>
				<Head>
					<title>OCP</title>
				</Head>
				<GlobalStyles />
				<MaintenanceMode />
			</>
		);
	}

	return (
		<>
			<Head>
				<title>OCP</title>
			</Head>
			<Provider store={authStore}>
				<QueryClientProvider client={queryClient}>
					<MDXProvider components={MDXComponents}>
						<Layout>
							<Component {...pageProps} />
						</Layout>
					</MDXProvider>
					{process.env.NODE_ENV === "development" && (
						<ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
					)}

					<ToastContainer />
					<GlobalStyles />
				</QueryClientProvider>
			</Provider>
		</>
	);
}
